import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  NumberInput,
  SegmentedControl,
  Checkbox,
  Textarea,
  Button,
  Group,
  Combobox,
  InputBase,
  useCombobox,
  ScrollArea,
  Alert,
  ActionIcon,
  Tooltip,
  Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useDosingChemicals } from '@/hooks/useDosingEvents';
import type { DosingSchedule, DosingScheduleCreateRequest } from '@/types/dosing';

interface DosingScheduleModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (data: DosingScheduleCreateRequest) => void;
  schedule?: DosingSchedule;
  isLoading?: boolean;
}

export function DosingScheduleModal({
  opened,
  onClose,
  onSubmit,
  schedule,
  isLoading,
}: DosingScheduleModalProps) {
  const isEditMode = !!schedule;

  // Form state
  const [chemicalName, setChemicalName] = useState('');
  const [chemicalSearch, setChemicalSearch] = useState('');
  const [volumeMl, setVolumeMl] = useState<number | string>(1.0);
  const [frequency, setFrequency] = useState<'daily' | 'weekly'>('daily');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isOngoing, setIsOngoing] = useState(true);
  const [brand, setBrand] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Chemicals autocomplete
  const { data: chemicalsData } = useDosingChemicals();
  const chemicals = chemicalsData?.chemicals ?? [];

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // Filter chemicals based on search
  const filteredChemicals = chemicals.filter((chem) =>
    chem.name.toLowerCase().includes(chemicalSearch.toLowerCase()),
  );

  // Date range for start: 1 year back to 1 year forward
  const minStartDate = new Date();
  minStartDate.setFullYear(minStartDate.getFullYear() - 1);
  const maxStartDate = new Date();
  maxStartDate.setFullYear(maxStartDate.getFullYear() + 1);

  // Populate form for edit mode, reset for create mode
  useEffect(() => {
    if (opened && schedule) {
      // Edit mode: populate from schedule
      setChemicalName(schedule.chemical_name);
      setChemicalSearch(schedule.chemical_name);
      setVolumeMl(schedule.volume_ml);
      setFrequency(schedule.frequency);
      setStartDate(new Date(schedule.start_date + 'T00:00:00'));
      if (schedule.end_date) {
        setEndDate(new Date(schedule.end_date + 'T00:00:00'));
        setIsOngoing(false);
      } else {
        setEndDate(null);
        setIsOngoing(true);
      }
      setBrand(schedule.brand ?? '');
      setNotes(schedule.notes ?? '');
      setError(null);
    } else if (opened && !schedule) {
      // Create mode: reset form
      setChemicalName('');
      setChemicalSearch('');
      setVolumeMl(1.0);
      setFrequency('daily');
      setStartDate(null);
      setEndDate(null);
      setIsOngoing(true);
      setBrand('');
      setNotes('');
      setError(null);
    }
  }, [opened, schedule]);

  const handleSubmit = () => {
    // Validate required fields
    if (!chemicalName.trim()) {
      setError('Please select or enter a chemical name.');
      return;
    }
    if (!volumeMl || Number(volumeMl) <= 0) {
      setError('Please enter a valid volume.');
      return;
    }
    if (!startDate) {
      setError('Please select a start date.');
      return;
    }

    const formatDate = (d: Date): string => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const data: DosingScheduleCreateRequest = {
      chemical_name: chemicalName.trim(),
      volume_ml: Number(volumeMl),
      frequency,
      start_date: formatDate(startDate),
      end_date: isOngoing ? null : endDate ? formatDate(endDate) : null,
      brand: brand.trim() || undefined,
      notes: notes.trim() || undefined,
    };

    onSubmit(data);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEditMode ? 'Edit Dosing Schedule' : 'Create Dosing Schedule'}
      size="md"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            withCloseButton
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Chemical Name - Combobox autocomplete */}
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => {
            setChemicalName(val);
            setChemicalSearch(val);
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              label="Chemical"
              aria-label="Chemical"
              placeholder="Search or enter chemical name"
              value={chemicalSearch}
              onChange={(event) => {
                setChemicalSearch(event.currentTarget.value);
                setChemicalName(event.currentTarget.value);
                combobox.openDropdown();
                combobox.updateSelectedOptionIndex();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              onBlur={() => combobox.closeDropdown()}
              rightSection={
                <Tooltip label="Type to search or select from the list">
                  <ActionIcon variant="subtle" size="sm">
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              }
            />
          </Combobox.Target>

          <Combobox.Dropdown>
            <Combobox.Options>
              <ScrollArea.Autosize mah={200}>
                {filteredChemicals.length === 0 ? (
                  <Combobox.Empty>No chemicals found</Combobox.Empty>
                ) : (
                  filteredChemicals.map((chem) => (
                    <Combobox.Option key={chem.id} value={chem.name}>
                      {chem.name}
                      {chem.category && (
                        <Text size="xs" c="dimmed" ml="xs" span>
                          ({chem.category})
                        </Text>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        {/* Volume */}
        <NumberInput
          label="Volume (mL)"
          aria-label="Volume"
          placeholder="Enter volume in mL"
          value={volumeMl}
          onChange={setVolumeMl}
          min={0.01}
          max={10000}
          step={0.5}
          decimalScale={1}
          required
        />

        {/* Frequency */}
        <div>
          <Text size="sm" fw={500} mb={4} component="label">
            Frequency
          </Text>
          <SegmentedControl
            value={frequency}
            onChange={(val) => setFrequency(val as 'daily' | 'weekly')}
            data={[
              { label: 'Daily', value: 'daily' },
              { label: 'Weekly', value: 'weekly' },
            ]}
            fullWidth
          />
        </div>

        {/* Start Date */}
        <DatePickerInput
          label="Start Date"
          placeholder="Select start date"
          value={startDate}
          onChange={(value) => setStartDate(value ? new Date(value) : null)}
          required
          disabled={isEditMode}
          minDate={minStartDate}
          maxDate={maxStartDate}
        />

        {/* End Date with Ongoing toggle */}
        <Checkbox
          label="Ongoing (no end date)"
          checked={isOngoing}
          onChange={(event) => {
            setIsOngoing(event.currentTarget.checked);
            if (event.currentTarget.checked) {
              setEndDate(null);
            }
          }}
        />

        {!isOngoing && (
          <DatePickerInput
            label="End Date"
            placeholder="Select end date"
            value={endDate}
            onChange={(value) => setEndDate(value ? new Date(value) : null)}
            minDate={startDate ?? undefined}
          />
        )}

        {/* Brand */}
        <TextInput
          label="Brand"
          placeholder="e.g. Reef Zlements, Red Sea"
          value={brand}
          onChange={(event) => setBrand(event.currentTarget.value)}
        />

        {/* Notes */}
        <Textarea
          label="Notes"
          placeholder="Optional notes about this schedule"
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
          minRows={2}
          autosize
        />

        {/* Action buttons */}
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={isLoading}>
            {isEditMode ? 'Save Changes' : 'Create Schedule'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
